import { useEffect, useRef, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { RootState } from 'App'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { BusinessUserType } from '__generated__/api-types-and-hooks'
import {
  getUserCalendarEventsAction,
  getUserCalendarEventsByUserIdAction,
} from 'store/actions/calendar'

interface IUseCalendarDataProps {
  showFindTimeCalendar: boolean
  selectedGuests: IAssigneeOptions[]
  findTimeCurrentDate: Date
}

interface ICalendarDataReturn {
  userAvailabilityMap: Record<string, any>
  pendingRequestsQueue: React.MutableRefObject<string[]>
  pendingGuestRequests: React.MutableRefObject<Record<string, boolean>>
  fetchedGuestsRef: React.MutableRefObject<Set<string>>
}

export const useCalendarData = ({
  showFindTimeCalendar,
  selectedGuests,
  findTimeCurrentDate,
}: IUseCalendarDataProps): ICalendarDataReturn => {
  const dispatch = useDispatch()
  const currentUser = useSelector((state: RootState) => state.user.user)
  const tenantId = useSelector((state: RootState) => state.user.tenantId)
  const userCalendarEvents = useSelector((state: RootState) => state.calendar.calendarEvents)
  const guestCalendarEvents = useSelector((state: RootState) => state.calendar.guestCalendarEvents)
  const userAvailabilityList = useSelector(
    (state: RootState) => state.calendar.userAvailabilityList
  )

  const [userAvailabilityMap, setUserAvailabilityMap] = useState<Record<string, any>>({})
  const pendingRequestsQueue = useRef<string[]>([])
  const pendingGuestRequests = useRef<Record<string, boolean>>({})
  const fetchedGuestsRef = useRef<Set<string>>(new Set())

  // Create organizer object
  const organizer: IAssigneeOptions = useMemo(() => {
    const firstName = currentUser.firstName || ''
    const lastName = currentUser.lastName || ''
    let label = `${firstName} ${lastName}`.trim()

    if (!label) {
      label = currentUser.name || currentUser.email || 'Organizer'
    }

    return {
      label,
      value: currentUser.id,
      type: BusinessUserType.Owner,
      user: {
        id: currentUser.id,
        firstName: firstName,
        lastName: lastName,
      },
    }
  }, [currentUser])

  // Get all attendees (organizer + selected guests)
  const allAttendees = useMemo(() => {
    return [organizer, ...selectedGuests]
  }, [organizer, selectedGuests])

  // Memoize internal attendees (excluding external emails)
  const internalAttendees = useMemo(() => {
    return allAttendees.filter((a) => a.value && !a.value.includes('@'))
  }, [allAttendees])

  // Fetch calendar data when attendees change
  useEffect(() => {
    if (
      allAttendees.length > 0 &&
      tenantId &&
      (showFindTimeCalendar || selectedGuests.length > 0)
    ) {
      const startDate = showFindTimeCalendar
        ? moment(findTimeCurrentDate).startOf('day')
        : moment().startOf('day')
      const endDate = showFindTimeCalendar
        ? moment(findTimeCurrentDate).add(7, 'days').endOf('day')
        : moment().add(7, 'days').endOf('day')

      const hasOrganizerEvents = Array.isArray(userCalendarEvents) && userCalendarEvents.length > 0

      if (
        hasOrganizerEvents &&
        !fetchedGuestsRef.current.has(currentUser.id) &&
        !currentUser.id.includes('@')
      ) {
        setUserAvailabilityMap((prev) => ({
          ...prev,
          [currentUser.id]: { type: 'events', data: userCalendarEvents },
        }))
        fetchedGuestsRef.current.add(currentUser.id)
      }

      internalAttendees.forEach((attendee) => {
        if (fetchedGuestsRef.current.has(attendee.value)) {
          return
        }

        if (pendingGuestRequests.current[attendee.value]) {
          return
        }

        pendingGuestRequests.current[attendee.value] = true
        fetchedGuestsRef.current.add(attendee.value)

        if (attendee.value === currentUser.id) {
          if (!hasOrganizerEvents) {
            dispatch(
              getUserCalendarEventsAction.STARTED({
                calendarName: '',
                timeMin: startDate.format('YYYY-MM-DDTHH:mm:ss-00:00'),
                timeMax: endDate.format('YYYY-MM-DDTHH:mm:ss-00:00'),
              })
            )
          }
        } else {
          dispatch(
            getUserCalendarEventsByUserIdAction.STARTED({
              userId: attendee.value,
              calendarName: '',
              timeMin: startDate.format('YYYY-MM-DDTHH:mm:ss-00:00'),
              timeMax: endDate.format('YYYY-MM-DDTHH:mm:ss-00:00'),
            })
          )
        }
      })
    }
  }, [
    allAttendees,
    tenantId,
    showFindTimeCalendar,
    selectedGuests.length,
    findTimeCurrentDate,
    dispatch,
    currentUser.id,
    userCalendarEvents,
    internalAttendees,
  ])

  // Update userAvailabilityMap with calendar events (current user only)
  useEffect(() => {
    if (
      Array.isArray(userCalendarEvents) &&
      userCalendarEvents.length > 0 &&
      !userAvailabilityMap[currentUser.id]
    ) {
      setUserAvailabilityMap((prev) => ({
        ...prev,
        [currentUser.id]: { type: 'events', data: userCalendarEvents },
      }))
    }
  }, [userCalendarEvents, currentUser.id, userAvailabilityMap])

  // Update userAvailabilityMap with guest calendar events
  useEffect(() => {
    if (
      guestCalendarEvents &&
      guestCalendarEvents.userId &&
      Array.isArray(guestCalendarEvents.events)
    ) {
      const guestId = guestCalendarEvents.userId
      const events = guestCalendarEvents.events

      setUserAvailabilityMap((prev) => ({
        ...prev,
        [guestId]: { type: 'events', data: events },
      }))

      if (pendingGuestRequests.current[guestId]) {
        pendingGuestRequests.current[guestId] = false
      }
    }
  }, [guestCalendarEvents, allAttendees])

  // Update userAvailabilityMap with availability data
  useEffect(() => {
    const availabilityData = userAvailabilityList?.availability
      ? userAvailabilityList
      : Array.isArray(userAvailabilityList) && userAvailabilityList.length > 0
      ? { availability: userAvailabilityList, duration: '60' }
      : null

    if (availabilityData && availabilityData.availability) {
      const userId = pendingRequestsQueue.current.shift()

      if (userId) {
        setUserAvailabilityMap((prev) => {
          if (userId === currentUser.id && prev[userId]?.type === 'events') {
            return prev
          }

          return {
            ...prev,
            [userId]: { type: 'availability', data: availabilityData },
          }
        })
      }
    }
  }, [userAvailabilityList, currentUser.id, allAttendees])

  return {
    userAvailabilityMap,
    pendingRequestsQueue,
    pendingGuestRequests,
    fetchedGuestsRef,
  }
}
